import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronRight, ExternalLink, ShieldAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/store/useAuthStore";
import { fetchAdminCenterNav } from "@/api/adminCenter";

/**
 * D-044 S0 — Admin Center shell (консолідуюча точка входу, БЕЗ екранів #1-#8).
 *
 * S0 scope: nav-скелет з backend (/Custom/admin-center/nav, admin-tier guard
 * server-side) + link-out набір (scope-split §1.3). Екрани S1-S5 розкочуються
 * слайсами; до того кожен disabled-стан має причину (§0 — 0 dead-CTA):
 * незбудований екран = явний badge «у розробці · S{n}», НЕ клікабельна заглушка.
 */
export function AdminCenterPage() {
  const perms = useAuthStore((s) => s.permissions);
  const canView = !!perms?.canViewAdminCenter;

  const navQ = useQuery({
    queryKey: ["adminCenter", "nav"],
    queryFn: ({ signal }) => fetchAdminCenterNav(signal),
    staleTime: 60_000,
    // Don't fire for non-admins — avoids a spurious 403 console error.
    enabled: canView,
  });

  if (!canView) {
    return (
      <div className="p-4 md:p-6">
        <div className="mx-auto w-full max-w-[640px]">
          <div className="rounded-lg border bg-muted/30 p-8 text-center">
            <ShieldAlert className="mx-auto mb-2 h-5 w-5 text-muted-foreground" />
            <p className="text-sm font-medium">Немає доступу до Адмін-центру</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Розділ доступний лише адміністраторам.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const data = navQ.data;

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto w-full max-w-[900px] space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Адмін-центр</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Користувачі, доступ, структура, маршрути, стан системи та аудит — єдина точка входу.
          </p>
        </div>

        {navQ.isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}

        {navQ.isError && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm">
            Не вдалося завантажити навігацію Адмін-центру.
          </div>
        )}

        {data && (
          <>
            <section>
              <h2 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Розділи
              </h2>
              <div className="grid gap-2 sm:grid-cols-2">
                {data.screens.map((s) =>
                  s.built ? (
                    <Link
                      key={s.key}
                      to={s.route}
                      className="flex items-center justify-between rounded-lg border bg-card px-4 py-3 text-sm transition-colors hover:bg-accent"
                    >
                      {s.label}
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </Link>
                  ) : (
                    <div
                      key={s.key}
                      className="flex items-center justify-between rounded-lg border bg-muted/20 px-4 py-3"
                      aria-disabled
                    >
                      <span className="text-sm text-muted-foreground">{s.label}</span>
                      {/* §0: disabled-стан з причиною — екран ще не збудований (слайс). */}
                      <Badge variant="outline" className="text-[10px] text-muted-foreground">
                        у розробці · {s.slice}
                      </Badge>
                    </div>
                  ),
                )}
              </div>
            </section>

            <section>
              <h2 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Технічне адміністрування (поза Адмін-центром)
              </h2>
              <div className="grid gap-1.5 sm:grid-cols-2">
                {data.linkOut.map((l) => (
                  <a
                    key={l.key}
                    href={l.href}
                    target={l.external ? "_blank" : undefined}
                    rel={l.external ? "noreferrer" : undefined}
                    className="flex items-center justify-between rounded-lg border px-4 py-2.5 text-sm transition-colors hover:bg-accent"
                  >
                    {l.label}
                    <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                  </a>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
